import { useState, useEffect } from "react";
import { getCached, setCached } from "./imageCache";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A successfully fetched image, ready to use as <img src={dataUri} /> */
export interface BlobImage {
  url: string;
  dataUri: string;
  mimeType: string;
  /** Size in bytes */
  size: number;
}

/** A URL that could not be fetched. */
export interface BlobImageError {
  url: string;
  error: string;
}

export type BlobImageResult = BlobImage | BlobImageError;

/** Type guard: did this URL resolve successfully? */
export function isBlobImage(result: BlobImageResult): result is BlobImage {
  return "dataUri" in result;
}

interface UseImagesState {
  images: BlobImageResult[];
  isLoading: boolean;
  error: string | null;
}

// ---------------------------------------------------------------------------
// Configuration — set REACT_APP_AZURE_FUNCTION_URL in your .env file.
// The env var holds the base host (e.g. http://localhost:7071); /api is
// appended here to match the Azure Functions routing convention.
// ---------------------------------------------------------------------------
const FUNCTION_BASE =
  `${process.env.REACT_APP_AZURE_FUNCTION_URL ?? "http://localhost:7071"}/api`;

const inFlightByUrl = new Map<string, Promise<BlobImageResult>>();

function getErrorMessage(err: unknown): string {
  return err instanceof Error ? err.message : "Unknown error fetching images";
}

async function fetchBlobImages(urls: string[], attempts = 2): Promise<Map<string, BlobImageResult>> {
  let lastError: unknown;

  for (let i = 0; i < attempts; i += 1) {
    try {
      const response = await fetch(`${FUNCTION_BASE}/blob-images`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ urls }),
      });

      if (!response.ok) {
        let message = `Server error ${response.status}`;
        try {
          const errBody = await response.json();
          if (errBody?.error) message = errBody.error;
        } catch {
          /* ignore */
        }
        throw new Error(message);
      }

      const data: { images: BlobImageResult[] } = await response.json();
      const freshByUrl = new Map<string, BlobImageResult>();

      for (const result of data.images) {
        freshByUrl.set(result.url, result);
      }

      return freshByUrl;
    } catch (err: unknown) {
      lastError = err;
      if (i === attempts - 1) throw err;
    }
  }

  throw lastError;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Fetches one or more images from Azure Blob Storage via the GetBlobImages
 * Azure Function and returns them as base64 data URIs.
 *
 * @param urls - Azure Blob Storage URLs to fetch.  Pass a stable array
 *               reference (or memo it) to avoid redundant refetches.
 *
 * @example
 * const { images, isLoading, error } = useImages([
 *   "https://storage4geodata.blob.core.windows.net/pano-data/tenant/img.jpg",
 * ]);
 *
 * return isLoading ? <Spinner /> : (
 *   images.map(r =>
 *     isBlobImage(r)
 *       ? <img key={r.url} src={r.dataUri} alt="" />
 *       : <p key={r.url}>Error: {r.error}</p>
 *   )
 * );
 */
export function useImages(urls: string[]): UseImagesState {
  const [state, setState] = useState<UseImagesState>({
    images: [],
    isLoading: false,
    error: null,
  });

  // Serialise the url list so the effect only re-runs when the contents change,
  // not when the caller passes a new array reference with the same values.
  const urlKey = urls.join("\n");

  useEffect(() => {
    let cancelled = false;

    if (urls.length === 0) {
      setState({ images: [], isLoading: false, error: null });
      return;
    }

    // ----- Cache check -----
    // Partition requested URLs into those already in cache and those that
    // still need to be fetched from Azure Blob Storage.
    const cachedResults = new Map<string, BlobImageResult>();
    const pendingResults = new Map<string, Promise<BlobImageResult>>();
    const uncachedUrls: string[] = [];

    for (const url of urls) {
      const hit = getCached(url);
      if (hit) {
        cachedResults.set(url, hit);
      } else {
        const inFlight = inFlightByUrl.get(url);
        if (inFlight) {
          pendingResults.set(url, inFlight);
        } else {
          uncachedUrls.push(url);
        }
      }
    }

    if (uncachedUrls.length === 0 && pendingResults.size === 0) {
      const images = urls.map((u) => cachedResults.get(u)!);
      setState({ images, isLoading: false, error: null });
      return;
    }

    const run = async () => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        if (uncachedUrls.length > 0) {
          const batchPromise = fetchBlobImages(uncachedUrls, 2);

          for (const url of uncachedUrls) {
            const urlPromise = batchPromise
              .then((freshByUrl) => freshByUrl.get(url) ?? { url, error: "Missing from response" })
              .catch((err: unknown) => ({ url, error: getErrorMessage(err) }));

            inFlightByUrl.set(url, urlPromise);
            pendingResults.set(url, urlPromise);

            urlPromise
              .then((result) => {
                setCached(url, result);
              })
              .finally(() => {
                if (inFlightByUrl.get(url) === urlPromise) {
                  inFlightByUrl.delete(url);
                }
              });
          }
        }

        const resolvedPending = new Map<string, BlobImageResult>();
        await Promise.all(
          Array.from(pendingResults.entries()).map(async ([url, promise]) => {
            const result = await promise;
            resolvedPending.set(url, result);
          }),
        );

        const merged: BlobImageResult[] = urls.map((url) => {
          return cachedResults.get(url) ?? resolvedPending.get(url) ?? { url, error: "Missing from response" };
        });

        if (cancelled) return;
        setState({ images: merged, isLoading: false, error: null });
      } catch (err: unknown) {
        if (cancelled) return;
        setState({
          images: [],
          isLoading: false,
          error: getErrorMessage(err),
        });
      }
    };

    run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlKey]);

  return state;
}
